import { useEffect, useState, type FormEvent } from 'react';
import { Navigate, useLocation } from 'react-router-dom';

import { isApiError } from '../api/errors';
import { DEFAULT_ROUTE } from '../app/module-registry';
import { Icon } from '../ui/icons';
import { FullPageSpinner } from '../ui/primitives';
import {
  BookingArt,
  CardWatermark,
  DashedFlightRule,
  EyeIcon,
  GroupArt,
  HeadsetIcon,
  LeadArt,
  LockIcon,
  MailIcon,
  PinArt,
  ReportArt,
  TourArt,
  TravellersArt,
  TrustArt,
} from './login-art';
import { DEV_AUTH_ENABLED, DEV_AUTH_HINT } from './dev-auth';
import { useAuth } from './useAuth';

interface LocationState {
  from?: string;
}

function messageFor(error: unknown): string {
  if (isApiError(error)) {
    if (error.code === 'INVALID_CREDENTIALS' || error.status === 401) {
      return 'Incorrect email, mobile number or password.';
    }
    if (error.code === 'VALIDATION_FAILED') {
      return 'Please enter a valid email or mobile number and password.';
    }
    if (error.status === 429) return 'Too many attempts. Please wait a moment and try again.';
    if (error.status >= 500) return 'The server is not responding. Please try again shortly.';
    return error.message;
  }
  return 'Could not reach the server. Check that the API is running.';
}

/**
 * The hero photograph, committed at packages/front/public/brand/. Local, so the
 * "no remote asset" guard in LoginPage.test.tsx still holds — do not swap this
 * for a CDN URL.
 *
 * The scrim over it is DELIBERATELY LIGHT. It used to be a near-opaque
 * white-to-charcoal wash sized for a flat gradient placeholder; laid over the
 * real photo it bleached the left half to paper and dropped a grey veil across
 * the mountains. The comp shows the photograph essentially untouched, so this
 * only lifts the top-left — where the headline and paragraph sit on open sky —
 * and fades to nothing before the artwork's focal point.
 *
 * The gradient stays ABOVE the photo in the layer list: CSS paints
 * `background-image` first-layer-on-top.
 *
 * The PNG is ~2.9MB, which is heavy for the one screen that has nothing warm in
 * cache. Worth re-exporting as a sized WebP with a JPEG fallback before launch —
 * flagged rather than done here, since re-encoding a brand asset is the
 * designer's call, and it is a `url()` swap when it happens.
 */
const HERO_SCRIM =
  'linear-gradient(102deg, rgba(255,255,255,.60) 0%, rgba(255,255,255,.34) 26%, rgba(255,255,255,.10) 46%, rgba(255,255,255,0) 62%)';

const HERO_PHOTO = 'url("/brand/gty-crm-login-bg.png")';

const HERO_BACKGROUND = [HERO_SCRIM, HERO_PHOTO].join(', ');

/**
 * No `outline-none` here, deliberately. It used to sit in this string and it
 * silently beat the global `:focus-visible { outline: 2px solid #e10600 }` in
 * `index.css` — same specificity, utilities layer wins — which left keyboard
 * users with only the soft `focus:ring` glow. The ring stays; the global outline
 * is now allowed to paint on top of it. Do not re-add `outline-none`.
 *
 * `pr-14` (56px) clears the password toggle's 44px hit target at its `right-2.5`
 * inset; `h-[48px]` is above the WCAG 2.5.5 target floor.
 */
const FIELD =
  'h-[48px] w-full rounded-[9px] border border-neutral-300 bg-white pl-[46px] pr-14 text-[13px] text-ink transition-colors placeholder:text-neutral-400 focus:border-brand focus:ring-4 focus:ring-brand/20';

/**
 * The hairline between a field's red glyph and its text, as drawn in the comp.
 * 30px tall at a 9px offset inside a 48px field, so it stops well short of the
 * border top and bottom and reads as a divider within the box rather than as a
 * second border crossing it.
 */
const FIELD_DIVIDER =
  'pointer-events-none absolute left-[38px] top-[9px] h-[30px] w-px bg-neutral-200';

const FEATURES = [
  { Art: LeadArt, title: 'Lead Management', description: 'Track & manage every lead effortlessly' },
  {
    Art: TourArt,
    title: 'Tour Package Management',
    description: 'Create, update & organize tours with ease',
  },
  {
    Art: BookingArt,
    title: 'Booking & Operations',
    description: 'Streamline bookings and day-to-day operations',
  },
  {
    Art: ReportArt,
    title: 'Reports & Analytics',
    description: 'Get real-time insights and grow your business',
  },
] as const;

/*
 * These are the comp's figures, set on the owner's instruction to match the
 * design exactly. They are LOWER than the 25,000+/150+/500+/10+ the build
 * previously carried, so this is a deliberate downward revision of a public
 * marketing claim, not a typo — if the higher numbers were the true ones, this
 * is the line to change back.
 */
const STATS = [
  { Art: TravellersArt, value: '10,000+', label: 'Happy Travellers' },
  { Art: PinArt, value: '15+', label: 'Destinations' },
  { Art: GroupArt, value: '100+', label: 'Group Tours' },
  { Art: TrustArt, value: '08+', label: 'Years of Trust' },
] as const;

export function LoginPage(): JSX.Element {
  const { status, login, sessionNotice, clearSessionNotice } = useAuth();
  const location = useLocation();

  const [identifier, setIdentifier] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => () => clearSessionNotice(), [clearSessionNotice]);

  if (status === 'booting') return <FullPageSpinner label="Restoring session" />;

  if (status === 'authenticated') {
    const from = (location.state as LocationState | null)?.from;
    return <Navigate to={from ?? DEFAULT_ROUTE} replace />;
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await login({ identifier: identifier.trim(), password });
    } catch (caught) {
      setError(messageFor(caught));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <main
      className="relative min-h-full bg-cover bg-center bg-no-repeat"
      style={{ backgroundImage: HERO_BACKGROUND }}
    >
      {/*
        CSS `order` moves the form above the hero on narrow screens VISUALLY
        only — the DOM stays hero-first at every breakpoint, so a screen-reader
        or keyboard user would otherwise wade through the headline, paragraph and
        four feature cards before reaching the first field. The hero is real,
        visible content and must not be `aria-hidden`; this link is the escape
        hatch instead, and it is the first focusable thing on the page.
      */}
      <a
        href="#identifier"
        className="sr-only focus:not-sr-only focus:absolute focus:z-50 focus:m-2 focus:rounded-[8px] focus:bg-white focus:px-4 focus:py-2 focus:text-ink focus:shadow-lg"
      >
        Skip to sign in
      </a>

      <div className="flex min-h-screen flex-col">
        {/* ── masthead: brand lockup ────────────────────────────────────── */}
        {/*
          The masthead gets its own, WIDER measure than the two columns below
          (1540 vs 1400). In the comp the lockup hangs outside the content
          column — logo at x≈90 against a headline at x≈168 — so sharing one
          container would pull it 60px right of where the design puts it.
        */}
        <header className="mx-auto flex w-full max-w-[1540px] items-start gap-4 px-5 pt-5 sm:px-8 lg:px-12 lg:pt-6">
          <div className="flex items-center gap-3.5">
            <img
              src="/brand/guruji-mark.png"
              alt="Guruji Travel Yatra"
              className="h-auto w-[48px] flex-none sm:w-[58px]"
            />
            {/*
              Three lines, one word each — that is the lockup in the comp, not a
              wrap that happens to fall there. Setting it as two lines and
              letting "Travel Yatra" break on its own put the tagline a line
              higher than the mark is tall, so the wordmark floated above the
              logo's optical centre instead of sitting flush with it.
            */}
            <div className="min-w-0">
              <div className="text-[17px] font-heading uppercase leading-[1.02] tracking-[.005em] text-ink sm:text-[23px]">
                Guruji
                <br />
                Travel
                <br />
                Yatra
              </div>
              <div className="mt-1.5 text-[9.5px] font-semibold leading-none text-neutral-700 sm:text-[11px]">
                Keep Guessing, Where&apos;s Next Trip?
              </div>
            </div>
          </div>
        </header>

        {/*
          ── the two columns ────────────────────────────────────────────────
          `minmax(0,2.06fr)` / `minmax(…,1fr)` rather than a bare `2.06fr 1fr`:
          the measured ratio is honoured wherever there is room, but at exactly
          1024px a raw split leaves the card too narrow to hold its own
          ornament. The floor keeps the card usable and the `minmax(0,…)` on the
          hero track lets long words shrink instead of forcing overflow.

          The floor is a FLAT 392px now. It used to step 400px → 460px at `xl`
          to absorb the card's padding growing from `lg:px-10` to `xl:px-14`;
          the padding no longer steps (it is `lg:px-9` at every width above
          `sm`), so a second, larger floor would only push the card wider than
          the comp draws it. 392px against 36px of padding a side leaves ~320px
          of content — clear of the AC-10 300px floor with margin.
        */}
        <div className="mx-auto grid w-full max-w-[1400px] flex-1 items-start gap-8 px-5 py-4 sm:px-8 lg:grid-cols-[minmax(0,2.1fr)_minmax(392px,1fr)] lg:gap-10 lg:px-12 xl:gap-14">
          {/*
            `justify-start`, not `justify-center`. Centring the hero inside a
            540px box balanced it against the sign-in card's height, which in
            the comp it is NOT — the headline starts above the card's top edge
            and the feature row finishes well above its bottom. Centred, the
            whole column sat ~40px low and the four cards drifted into the
            stats bar at 900px tall.
          */}
          <section className="order-2 flex min-w-0 flex-col justify-start lg:order-1 lg:min-h-[540px]">
            <Hero />
          </section>

          <section className="order-1 flex min-w-0 justify-center lg:order-2 lg:justify-end">
            {/*
              `overflow-hidden` is load-bearing, not decoration: it clips the
              watermark to the card's rounded box AND, together with
              `break-words` on the error/notice blocks below, keeps an
              arbitrarily long server message from pushing the page sideways.
            */}
            <div className="relative w-full max-w-[440px] overflow-hidden rounded-[18px] border border-white/80 bg-surface-raised shadow-[0_28px_90px_rgba(0,0,0,.30)]">
              <CardWatermark className="pointer-events-none absolute inset-0 h-full w-full text-brand/[.10]" />

              <div className="relative px-6 py-8 sm:px-9 sm:py-9 lg:px-9 lg:py-10">
                <div className="text-center">
                  {/*
                    Set in the app's own heavy face, NOT a script one. This
                    carried a `'Segoe Script', … , cursive` stack chasing the
                    comp's display lettering, but that stack resolves to a
                    different handwriting face on every OS and to a generic
                    cursive on most Linux and Android — three renderings, none of
                    them the comp's. The comp's word is a heavy geometric sans
                    with even stroke weight, which Archivo 800 is, so this is
                    both closer to the design AND identical everywhere.
                  */}
                  <h2 className="text-[27px] font-heading leading-[1.05] tracking-[-.01em] text-brand sm:text-[31px]">
                    Welcome Back!
                  </h2>
                  <p className="mt-1.5 text-[12.5px] font-semibold leading-[1.4] text-neutral-800">
                    Sign in to your GTY CRM Account
                  </p>

                  <DashedFlightRule className="mx-auto mt-3.5 w-full max-w-[175px] text-brand/55" />
                </div>

                <form onSubmit={(e) => void handleSubmit(e)} noValidate className="mt-7">
                  {sessionNotice !== null && (
                    <p className="mb-4 break-words rounded-[10px] border border-warning/40 bg-warning/10 px-3 py-2.5 text-[12.5px] leading-[1.45] text-warning-ink">
                      {sessionNotice}
                    </p>
                  )}

                  <label htmlFor="identifier" className="mb-2 block text-[12.5px] font-bold text-ink">
                    Email or Mobile Number
                  </label>
                  <div className="relative mb-4">
                    <MailIcon
                      size={18}
                      className="pointer-events-none absolute left-[13px] top-1/2 -translate-y-1/2 text-brand"
                    />
                    <span className={FIELD_DIVIDER} />
                    <input
                      id="identifier"
                      name="identifier"
                      type="text"
                      inputMode="email"
                      autoComplete="username"
                      required
                      value={identifier}
                      onChange={(e) => setIdentifier(e.target.value)}
                      className={FIELD}
                      placeholder="Enter your email or mobile number"
                    />
                  </div>

                  <div className="mb-2 flex items-center gap-3">
                    <label htmlFor="password" className="block text-[12.5px] font-bold text-ink">
                      Password
                    </label>
                    <a
                      href="#"
                      className="ml-auto text-[12.5px] font-semibold text-brand hover:underline"
                    >
                      Forgot Password?
                    </a>
                  </div>
                  <div className="relative mb-4">
                    <LockIcon
                      size={18}
                      className="pointer-events-none absolute left-[13px] top-1/2 -translate-y-1/2 text-brand"
                    />
                    <span className={FIELD_DIVIDER} />
                    <input
                      id="password"
                      name="password"
                      type={showPassword ? 'text' : 'password'}
                      autoComplete="current-password"
                      required
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className={FIELD}
                      placeholder="Enter Your Password"
                    />
                    {/*
                      The accessible name is "Show password" / "Hide password",
                      which CONTAINS the word "password" — that is what made a
                      loose `getByLabel('Password')` in the E2E helper resolve to
                      two elements. The helper matches exactly; the name here
                      stays descriptive because that is what a screen reader
                      needs to hear.

                      The 44×44 WCAG 2.5.5 target lives on the BUTTON. The eye
                      itself stays ~19px and the hover disc stays 36px on an
                      inner span — a 44px grey circle at this `right-2.5` inset
                      would spill over the input's own border.
                    */}
                    <button
                      type="button"
                      aria-label={showPassword ? 'Hide password' : 'Show password'}
                      aria-pressed={showPassword}
                      onClick={() => setShowPassword((current) => !current)}
                      className="group absolute right-2.5 top-1/2 grid h-11 w-11 -translate-y-1/2 place-items-center text-neutral-500 transition-colors hover:text-ink"
                    >
                      <span className="grid h-9 w-9 place-items-center rounded-full transition-colors group-hover:bg-neutral-100">
                        <EyeIcon off={showPassword} />
                      </span>
                    </button>
                  </div>

                  {/*
                    "Remember me" is INTERACTIVE but not yet wired to session
                    lifetime — that is a server-side decision (the refresh token
                    is an HttpOnly cookie and the access token is deliberately
                    memory-only), so honouring it means varying that cookie's
                    max-age on the API. Left visible to match the design and
                    flagged rather than shipped as a permanently-ticked disabled
                    box, which would look like it was already doing something.
                  */}
                  <label className="mb-5 flex w-fit cursor-pointer items-center gap-2.5 py-1.5 text-[12.5px] font-medium text-neutral-800">
                    <input
                      type="checkbox"
                      checked={rememberMe}
                      onChange={(e) => setRememberMe(e.target.checked)}
                      className="h-[17px] w-[17px] flex-none accent-brand"
                    />
                    Remember me
                  </label>

                  {error !== null && (
                    <p
                      role="alert"
                      className="mb-4 break-words rounded-[10px] border border-danger-border bg-danger-bg px-3 py-2.5 text-[12.5px] leading-[1.45] text-danger-ink"
                    >
                      {error}
                    </p>
                  )}

                  {/*
                    The disabled state paints an explicit grey palette rather
                    than dimming the gradient with `opacity`. This is the page's
                    only primary action and it is disabled on arrival, so it is
                    the first thing most people see: a 55%-opacity white label on
                    red composites to ~2.8:1, while `neutral-300` + `neutral-800`
                    reads at ~6.8:1. The `disabled={…}` CONDITION is unchanged.
                  */}
                  <button
                    type="submit"
                    disabled={submitting || identifier.length === 0 || password.length === 0}
                    className="flex h-[50px] w-full items-center justify-center gap-3 rounded-[9px] bg-gradient-to-r from-brand to-brand-pressed pl-5 pr-2.5 text-[14px] font-bold text-white shadow-brand transition-[filter] hover:brightness-110 disabled:cursor-not-allowed disabled:bg-neutral-300 disabled:bg-none disabled:text-neutral-800 disabled:shadow-none disabled:hover:brightness-100"
                  >
                    <span className="flex-1 text-center">{submitting ? 'Signing in…' : 'Login'}</span>
                    <span className="grid h-[34px] w-[34px] flex-none place-items-center rounded-full bg-white text-brand">
                      <Icon name="arrow-right" size={17} strokeWidth={2.6} />
                    </span>
                  </button>
                </form>

                {DEV_AUTH_ENABLED && (
                  <div className="mt-5 rounded-[10px] border border-dashed border-warning/60 bg-warning/10 px-3.5 py-3">
                    <p className="text-[11px] font-bold uppercase leading-none tracking-caps text-warning-ink">
                      Dev stub — no backend
                    </p>
                    <p className="mt-2 text-[12.5px] leading-[1.5] text-warning-ink">
                      Sign in with{' '}
                      <button
                        type="button"
                        onClick={() => {
                          setIdentifier(DEV_AUTH_HINT.email);
                          setPassword(DEV_AUTH_HINT.password);
                        }}
                        className="font-semibold underline underline-offset-2"
                      >
                        {DEV_AUTH_HINT.email} / {DEV_AUTH_HINT.password}
                      </button>
                      . Viewing as <strong>{DEV_AUTH_HINT.role()}</strong>.
                    </p>
                  </div>
                )}

                <div className="mt-5 flex items-center justify-center gap-2 text-[12px] text-neutral-800">
                  <HeadsetIcon size={16} className="text-brand" />
                  <span className="font-medium">Need help?</span>
                  <a href="#" className="font-semibold text-brand hover:underline">
                    Contact Support
                  </a>
                </div>
              </div>
            </div>
          </section>
        </div>

        <StatsBar />

        {/*
          NOT IN THE COMP — the design image stops at the stats bar. Kept because
          the copyright line and the Privacy / Terms links are a legal fixture of
          the signed-out page, not a design element, and an e2e assertion pins
          them; dropping them to match the picture is a product call, not an
          implementation one. Ask if they should go.

          The scrim under it is its own, because contrast here cannot be assumed
          from the artwork: the photograph's bottom edge is a bright road, and
          white type would vanish on it unmodified. Softened from black/45 to
          black/35 now that a real photo sits behind it rather than a flat
          gradient — 45% read as a dark band laid across the road.
        */}
        <footer className="bg-gradient-to-t from-black/35 to-transparent px-5 pb-4 pt-6 text-center text-[11.5px] font-medium text-white/90">
          <span>© 2025 Guruji Travel Yatra. All Rights Reserved.</span>
          <Separator />
          <a href="#" className="hover:underline">
            Privacy Policy
          </a>
          <Separator />
          <a href="#" className="hover:underline">
            Terms &amp; Conditions
          </a>
          <Separator />
          <span>v2.0.0</span>
        </footer>
      </div>
    </main>
  );
}

function Separator(): JSX.Element {
  return <span className="mx-2.5 text-white/40 sm:mx-4">|</span>;
}

function Hero(): JSX.Element {
  return (
    <div className="min-w-0 max-w-[860px]">
      {/*
        Sized off the comp, not off "what a hero headline usually is". Measured
        against the 1600px design: "Manage" is ~112px wide there, which puts the
        face at ~31px — a good deal smaller than the 47-64px this used to run.
        The oversized version pushed the paragraph and the feature row down far
        enough that the four cards collided with the stats bar at 900px tall.
      */}
      <h1 className="text-[26px] font-heading leading-[1.14] tracking-[-.01em] text-ink sm:text-[30px] lg:text-[32px] xl:text-[36px]">
        Manage <span className="text-brand">Journeys.</span>
        <br />
        Create <span className="text-brand">Memories.</span>
      </h1>

      <div className="mt-3.5 h-[4px] w-[54px] rounded-full bg-brand" />

      {/*
        The comp sets the lead-in as one measure under the rule, with "Welcome to
        GTY CRM –" in brand red and the rest in ink. The em dash is an EN dash
        with spaces in the comp; kept as such rather than normalised to "—",
        since the coloured run has to break at the same place to wrap over two
        lines the way the design does.
      */}
      <p className="mt-4 max-w-[570px] text-[16.5px] font-semibold leading-[1.5] text-ink">
        <span className="text-brand">Welcome to GTY CRM</span> – your all-in-one platform to manage
        leads, bookings, tours and happy travellers.
      </p>

      <div className="mt-7 grid grid-cols-1 gap-3.5 sm:grid-cols-2 sm:gap-4 lg:grid-cols-4 lg:gap-3.5 xl:gap-4">
        {FEATURES.map(({ Art, title, description }) => (
          <FeatureCard key={title} Art={Art} title={title} description={description} />
        ))}
      </div>
    </div>
  );
}

/**
 * A marketing tile, NOT a document section — hence a styled `<p>` for the title
 * rather than an `<h3>`. The hero is DOM-first, so four `h3`s here would land
 * between the page's `h1` and the card's `h2` and read as `h1 → h3 → h2`: both a
 * skipped level and an out-of-order one. Nothing links to these tiles and no
 * landmark needs them, so demoting them costs nothing.
 */
function FeatureCard({
  Art,
  title,
  description,
}: {
  Art: (props: { className?: string; size?: number }) => JSX.Element;
  title: string;
  description: string;
}): JSX.Element {
  return (
    <article className="min-w-0 rounded-[14px] border border-white/70 bg-white/[.97] px-5 py-5 text-center shadow-[0_16px_44px_rgba(0,0,0,.16)] backdrop-blur-sm">
      <Art className="mx-auto text-brand" />
      {/*
        The `max-w` is what makes every title break onto TWO lines, as the comp
        draws them ("Booking &" / "Operations"). Left to the card's own width
        three of the four fitted on one line, so the four cards came out at
        different heights in a row that the design keeps flush. An `em` measure,
        not a pixel one, so it tracks the font size rather than needing a second
        value at each breakpoint.
      */}
      <p className="mx-auto mt-3 max-w-[7.6em] text-[15px] font-extrabold leading-[1.22] text-ink">
        {title}
      </p>
      <DashedFlightRule className="mx-auto mt-2.5 w-[78%] text-brand/50" />
      <p className="mt-2.5 text-[11.5px] font-medium leading-[1.45] text-neutral-700">
        {description}
      </p>
    </article>
  );
}

function StatsBar(): JSX.Element {
  return (
    // Inset, not full-bleed. `max-w-[1340px]` puts the bar's edges at x=130 on
    // the 1600px comp, which is where the design draws them — a hair wider than
    // the feature row above it, so the two read as a set rather than as one bar
    // that ran to the window edge.
    <div className="mx-auto mb-3 w-[calc(100%-40px)] max-w-[1340px] rounded-[14px] border border-white/70 bg-white/[.97] px-6 py-[18px] shadow-[0_18px_50px_rgba(0,0,0,.22)] backdrop-blur-sm sm:px-10">
      {/*
        No `lg:border-l` dividers between cards. The design draws none, and the
        rule only ever painted correctly on the exact column count it was written
        for — at `grid-cols-2` it struck a line down the middle of the bar.
      */}
      <div className="grid grid-cols-2 gap-y-5 lg:grid-cols-4">
        {STATS.map(({ Art, value, label }) => (
          <StatCard key={label} Art={Art} value={value} label={label} />
        ))}
      </div>
    </div>
  );
}

function StatCard({
  Art,
  value,
  label,
}: {
  Art: (props: { className?: string; size?: number }) => JSX.Element;
  value: string;
  label: string;
}): JSX.Element {
  return (
    <div className="flex min-w-0 items-center justify-center gap-3.5">
      <Art className="flex-none text-brand" />
      <div className="min-w-0">
        <div className="text-[19px] font-extrabold leading-none text-ink">{value}</div>
        <div className="mt-1.5 text-[12.5px] font-medium leading-none text-neutral-800">{label}</div>
      </div>
    </div>
  );
}
